/* eslint-disable @typescript-eslint/no-explicit-any */
type ScrollAnimation = {
  timeout?: TimeoutID;
  callback: (...args: Array<any>) => any;
};

type SavedPageState = {
  scrollLeft: number;
  scrollTop: number;
  htmlStyle: string | null;
  bodyStyle: string | null;
};

let savedPageState: SavedPageState | null = null;
let scrollAnimation: ScrollAnimation | null | undefined;
let emptyFunction = () => {};

const PUNCTUATION: ObjectOf<boolean> = { '.': true, '!': true, '?': true };
const TRAILING_SPACE = /\s+$/;
const TRAILING_COLONS = /(\s*:)+$/;
const SCROLL_ANIMATION_REFRESH_MS = 20;

export function disablePageScroll() {
  if (savedPageState) {
    return;
  }
  if (!document.documentElement || !document.body) {
    return;
  }
  let html = document.documentElement;
  let body = document.body;
  savedPageState = {
    scrollLeft: window.pageXOffset, // or window.scrollX ?
    scrollTop: window.pageYOffset, // or window.scrollY ?
    htmlStyle: html.getAttribute('style'),
    bodyStyle: body.getAttribute('style'),
  };
  let currentWidth = body.clientWidth;
  let currentHeight = body.clientHeight;
  body.style.width = currentWidth + 'px';
  body.style.height = currentHeight + 'px';
  html.style.height = '100%';
  html.style.overflow = 'hidden';
}

export function enablePageScroll() {
  if (!savedPageState) {
    return;
  }
  if (!document.documentElement || !document.body) {
    return;
  }
  let html = document.documentElement;
  let body = document.body;
  if (savedPageState.htmlStyle == null) {
    html.removeAttribute('style');
  } else {
    html.setAttribute('style', savedPageState.htmlStyle);
  }
  if (savedPageState.bodyStyle == null) {
    body.removeAttribute('style');
  } else {
    body.setAttribute('style', savedPageState.bodyStyle);
  }
  window.scrollTo(savedPageState.scrollLeft, savedPageState.scrollTop);
  savedPageState = null;
}

export function capFirst(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function trimTrailingColon(name: string | null | undefined) {
  if (name == null) {
    return '';
  }
  name = String(name);
  name = name.replace(TRAILING_SPACE, '');
  name = name.replace(TRAILING_COLONS, '');
  return name;
}

export function addTrailingColon(name: string | null | undefined) {
  name = trimTrailingColon(name);
  if (name === '') {
    return name;
  }
  let lastChar = name.slice(-1);
  if (PUNCTUATION[lastChar] !== true) {
    name = name + ':';
  }
  return name;
}

export function loadScript(src: string) {
  let promise: Promise<unknown> = new Promise((resolve, reject) => {
    let script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', resolve);
    script.addEventListener('error', (event: Event) => {
      script.parentNode && script.parentNode.removeChild(script);
      reject(event);
    });
    document.head && document.head.appendChild(script);
  });
  return promise;
}

export function throttle(fn: () => void, minDelta: number) {
  let lastCalledAt: number | undefined;
  let timeout: number | undefined;
  return function self() {
    clearTimeout(timeout);
    let now = Date.now();
    let timeSinceLastChange =
      lastCalledAt == null ? Infinity : now - lastCalledAt;
    if (timeSinceLastChange < minDelta) {
      timeout = setTimeout(self, minDelta - timeSinceLastChange) as any;
    } else {
      lastCalledAt = now;
      fn();
    }
  };
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function debounce(fn: Function, minDelay: number) {
  let timeout: number | undefined;
  return (...args: Array<unknown>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), minDelay) as any;
  };
}

// If the top of the element is not in view, scroll it to the window top.
export function scrollTopIntoView(element: HTMLElement, duration: number) {
  let windowTop = window.pageYOffset;
  let windowBottom = windowTop + window.innerHeight;
  let nodeTop = getPageOffsetTop(element);
  if (nodeTop < windowTop || nodeTop > windowBottom) {
    scrollTo(nodeTop, duration);
  }
}

export function getPageOffsetTop(element_: HTMLElement) {
  let element: HTMLElement | null = element_;
  let offsetTop = 0;
  while (element) {
    offsetTop += element.offsetTop;
    element = element.offsetParent as HTMLElement | null;
  }
  return offsetTop;
}

export function scrollToBottom(
  duration: number,
  callback: (...args: Array<any>) => any,
) {
  if (document.body) {
    scrollTo(
      document.body.clientHeight - window.innerHeight,
      duration,
      callback,
    );
  }
}

export function scrollTo(
  to: number,
  duration: number,
  callback?: ((...args: Array<any>) => any) | null | undefined,
) {
  let pageYOffset = window.pageYOffset;
  let pageXOffset = window.pageXOffset;
  let change = to - pageYOffset;
  let currentTime = 0;
  callback = callback || emptyFunction;
  // if currently doing an animation, cancel it and queue its callback
  if (scrollAnimation) {
    clearTimeout(scrollAnimation.timeout);
    callback = combineCallbacks(scrollAnimation.callback, callback);
  }
  scrollAnimation = { callback: callback };
  (function animateScroll() {
    currentTime += SCROLL_ANIMATION_REFRESH_MS;
    window.scrollTo(
      pageXOffset,
      easeInOutQuad(currentTime, pageYOffset, change, duration),
    );
    if (scrollAnimation) {
      if (currentTime < duration) {
        scrollAnimation.timeout = setTimeout(
          animateScroll,
          SCROLL_ANIMATION_REFRESH_MS,
        );
      } else {
        scrollAnimation.callback();
      }
    }
  })();
}

export function combineCallbacks(
  ...funcs: Array<(...args: Array<any>) => any>
) {
  return (...args: Array<unknown>) => {
    funcs.forEach((fn) => fn(...args));
  };
}

export function fitToContain(
  srcWidth: number,
  srcHeight: number,
  targetWidth: number,
  targetHeight: number,
) {
  let width;
  let height;
  if (srcWidth / srcHeight > targetWidth / targetHeight) {
    width = targetWidth;
    height = Math.round((width / srcWidth) * srcHeight);
  } else {
    height = targetHeight;
    width = Math.round((height / srcHeight) * srcWidth);
  }
  return { width, height };
}

export function fitToCover(
  srcWidth: number,
  srcHeight: number,
  targetWidth: number,
  targetHeight: number,
) {
  let width;
  let height;
  if (srcWidth / srcHeight < targetWidth / targetHeight) {
    width = targetWidth;
    height = Math.round((width / srcWidth) * srcHeight);
  } else {
    height = targetHeight;
    width = Math.round((height / srcHeight) * srcWidth);
  }
  return { width, height };
}

function easeInOutQuad(t: number, b: number, c: number, d: number) {
  t /= d / 2;
  if (t < 1) {
    return (c / 2) * t * t + b;
  }
  t--;
  return (-c / 2) * (t * (t - 2) - 1) + b;
}
